import type { Design, Masjid, PowerState, TimetableDay, Widget } from '@noorview/core';
import { FONT_LIST, LED_DEFAULT, LED_FONT, PATTERN_SIZES, WIDGETS, isLed, canScaleContent, designOf, cornerRadius, fontStack, gridMetrics, salahBlankAt, patternById, type BackgroundPattern, indexTimetable, isLightBackground, prayerState, rectToPx, rgba, rgbToHex, widgetLook, ymd, hhmm, mix, makruhAt, MAKRUH_RED, MAKRUH_INK, type MakruhProps } from '@noorview/core';
import { ensureStyles } from './styles';
import { FACTORIES, el, makeDayCache, type Env, type Tick, type WidgetView } from './widgets';
import { BroadcastLayer } from './broadcast';
import { KhutbahLayer } from './khutbah';
import type { Broadcast, KhutbahColours, KhutbahLine } from '@noorview/core';
import { isBroadcastLive } from '@noorview/core';

export { ensureStyles, DISPLAY_CSS } from './styles';

/** Inner margin (px at the screen's native size) kept clear when content is scaled with the Content size slider. */
const FIT_PAD = 16;

let patternSeq = 0;
/**
 * The background pattern as one SVG: a repeating tile, optionally masked to a soft border or the four corners.
 * Built from the app's own pattern shapes and a validated colour, so it's safe to insert as markup.
 */
function patternLayer(p: BackgroundPattern, m: { width: number; height: number }): HTMLElement {
  const def = patternById(p.id);
  const box = el('div', 'nvd-pat');
  if (!def || !/^#[0-9a-f]{6}$/i.test(p.color)) return box;
  const { width: W, height: H } = m;
  const short = Math.min(W, H);
  const size = PATTERN_SIZES[p.size] ?? PATTERN_SIZES.medium;
  const sc = (size * short) / 1080 / 100;
  const id = `nvp${++patternSeq}`;
  let mask = '', useMask = '';
  if (p.coverage === 'border') {
    const t = Math.round(short * 0.11);
    mask = `<mask id="${id}m"><rect width="${W}" height="${H}" fill="#fff"/><rect x="${t}" y="${t}" width="${W - 2 * t}" height="${H - 2 * t}" rx="${t * 0.4}" fill="#000" filter="url(#${id}b)"/></mask><filter id="${id}b" x="-10%" y="-10%" width="120%" height="120%"><feGaussianBlur stdDeviation="${(t * 0.18).toFixed(1)}"/></filter>`;
    useMask = ` mask="url(#${id}m)"`;
  } else if (p.coverage === 'corners') {
    const rad = Math.round(short * 0.55);
    const g = `<radialGradient id="${id}g"><stop offset="0" stop-color="#fff"/><stop offset=".55" stop-color="#fff" stop-opacity=".7"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>`;
    mask = `${g}<mask id="${id}m"><rect width="${W}" height="${H}" fill="#000"/>${[[0, 0], [W, 0], [0, H], [W, H]].map(([x, y]) => `<circle cx="${x}" cy="${y}" r="${rad}" fill="url(#${id}g)"/>`).join('')}</mask>`;
    useMask = ` mask="url(#${id}m)"`;
  }
  box.style.opacity = String(Math.max(0, Math.min(100, p.opacity)) / 100);
  box.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" aria-hidden="true"><defs>${mask}<pattern id="${id}" patternUnits="userSpaceOnUse" width="${def.w * sc}" height="${def.h * sc}"><g transform="scale(${sc})" fill="none" stroke="${p.color}" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round">${def.svg}</g></pattern></defs><rect width="${W}" height="${H}" fill="url(#${id})"${useMask}/></svg>`;
  return box;
}
export { qrSvg } from './widgets';

/** Kept for older imports; the font catalogue now lives in @noorview/core. */
export const FONT_STACKS: Record<string, string> = Object.fromEntries(FONT_LIST.map((f) => [f.name, f.stack]));

export interface StageOptions {
  mode: Env['mode'];
  masjid: Masjid;
  timetable: readonly TimetableDay[];
  /** 'high' blurs behind glass widgets; 'balanced' uses an equivalent tint, for low-power devices. */
  quality?: 'high' | 'balanced';
  /** Clock source; the Studio can preview a different time. */
  now?: () => Date;
  /** How the stage fits its host: 'contain' letterboxes, 'cover' fills. */
  fit?: 'contain' | 'cover';
  /** Called after the stage is scaled, with the scale and the stage's offset inside the host. */
  onScale?: (s: { scale: number; left: number; top: number; width: number; height: number }) => void;
  /** Stop the once-a-second clock (for still thumbnails). */
  frozen?: boolean;
  /**
   * Blank during salah when the masjid's Salah mode is on. A real screen does unless its own switch is off (false); any
   * other picture (a preview of what's live on a screen) only when asked (true). Studio canvases, design thumbnails,
   * templates and style previews never do: they show the design, not what's on a screen right now.
   */
  salahBlank?: boolean;
  /** The screen's name, shown small at the bottom left (see the footer). */
  screenName?: string;
  /** The screen this is (a real screen, or a preview of one): a live khutbah shows only on the screens it's for. */
  screenId?: string;
}

/** The small print along the bottom edge, right: who makes NoorView. */
export const FOOTER_CREDIT = 'A product of Noor Management Systems';

/** Text-fit results shared by every stage on the page (see fit). */
const FIT_CACHE = new Map<string, number>();
/** The automatic size, remembered the same way: opening a page full of previews (the Studio, the dashboard) no longer
 *  measures every widget again. */
const AUTO_CACHE = new Map<string, number>();

interface Mounted { w: Widget; sig: string; /** The same without Content size, which doesn't change the largest size that fits. */ base?: string; root: HTMLElement; surf: HTMLElement; content: HTMLElement; view: WidgetView; env: Env; kmax?: number; kkey?: string }

/**
 * A live NoorView display inside `host`. The same class renders the real screen, the Studio canvas and every preview.
 * It only rebuilds widgets whose settings changed, and the once-a-second tick only touches text that changed.
 */
export class Stage {
  readonly host: HTMLElement;
  readonly stage: HTMLElement;
  /** Layer above the widgets, in display coordinates (used by the Studio for handles). */
  readonly overlay: HTMLElement;
  private bg: HTMLElement;
  private power: HTMLElement;
  private salah: HTMLElement;
  private salahClock: HTMLElement;
  private salahOn = false;
  private powerClock: HTMLElement;
  /** The footer: a whisper of text inside the bottom border (screen name left, credit right), never over a widget. */
  private foot: HTMLElement;
  private footName: HTMLElement;
  private bcs = new BroadcastLayer();
  private kh = new KhutbahLayer();
  private bcList: readonly Broadcast[] = [];
  private mounted = new Map<string, Mounted>();
  private design: Design | null = null;
  private opts: StageOptions;
  private timetable;
  private dayCache = makeDayCache();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ro: ResizeObserver | null = null;
  private dataSig = '';
  private powerState: PowerState = 'on';
  scale = 1;

  constructor(host: HTMLElement, opts: StageOptions) {
    ensureStyles(host.ownerDocument);
    this.host = host;
    this.opts = opts;
    this.timetable = indexTimetable(opts.timetable);
    host.classList.add('nvd-host');
    const view = host.ownerDocument.defaultView;
    if (view && view.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    this.stage = el('div', `nvd-stage q-${opts.quality ?? 'high'}${opts.mode === 'editor' ? ' nvd-edit' : ''}`);
    if (opts.mode === 'editor') { host.style.contain = 'size layout'; host.style.overflow = 'visible'; }
    this.bg = el('div', 'nvd-bg');
    this.overlay = el('div', 'nvd-overlay');
    this.overlay.style.cssText = 'position:absolute;inset:0;z-index:1000;pointer-events:none';
    this.salah = el('div', 'nvd-salah');
    this.salahClock = el('div', 'nvd-salah-ck');
    this.salah.append(this.salahClock);
    this.power = el('div', 'nvd-power');
    this.powerClock = el('div', 'ck');
    this.power.append(this.powerClock);
    this.foot = el('div', 'nvd-foot');
    this.footName = el('span');
    const credit = el('span');
    credit.textContent = FOOTER_CREDIT;
    this.foot.append(this.footName, credit);
    this.foot.setAttribute('aria-hidden', 'true');
    this.stage.append(this.bg, this.foot, this.overlay, this.salah, this.bcs.root, this.power, this.kh.root);
    this.placeFooter();
    host.append(this.stage);
    if (typeof ResizeObserver !== 'undefined') { this.ro = new ResizeObserver(() => this.layout()); this.ro.observe(host); }
    if (!opts.frozen) this.schedule();
  }

  private now() { return this.opts.now ? this.opts.now() : new Date(); }

  setData(masjid: Masjid, timetable: readonly TimetableDay[]) {
    const sig = JSON.stringify(masjid) + timetable.length + (timetable[0]?.date ?? '') + (timetable.at(-1)?.date ?? '');
    this.opts.masjid = masjid;
    this.timetable = indexTimetable(timetable);
    this.dayCache = makeDayCache();
    if (sig !== this.dataSig) { this.dataSig = sig; for (const m of this.mounted.values()) { m.env = this.envFor(m.w); m.view.update(m.w, m.env); } this.tick(true); }
  }

  setOptions(o: Partial<Pick<StageOptions, 'now' | 'quality' | 'mode' | 'salahBlank' | 'screenName' | 'screenId'>>) {
    Object.assign(this.opts, o);
    this.placeFooter();
    this.stage.className = `nvd-stage q-${this.opts.quality ?? 'high'}${this.opts.mode === 'editor' ? ' nvd-edit' : ''}${this.stage.classList.contains('on-light') ? ' on-light' : ''}`;
    this.tick(true);
  }

  /** Live messages and appeals for this screen. Never shown in the Studio editor. */
  setBroadcasts(list: readonly Broadcast[]) { this.bcList = [...list]; this.bcs.set(list); this.tickBroadcasts(this.now()); }

  /** What was said in the live khutbah so far (from the screen's live feed); lines may come again, translated. */
  setKhutbahLines(lines: readonly KhutbahLine[]) { this.kh.setLines(lines); }
  /** Previews: the live khutbah's sample, looping, in these colours. */
  playKhutbahSample(colours: KhutbahColours) { this.kh.playSample(colours); }
  /** The live khutbah's colours, while previewing it. */
  setKhutbahColours(colours: KhutbahColours) { this.kh.colours(colours); }

  private tickBroadcasts(now: Date) {
    // In the Studio they show over the design as on the screen, but never take a click meant for the widgets.
    if (this.opts.mode === 'editor') this.bcs.root.style.pointerEvents = 'none';
    this.bcs.tick(now, this.design, undefined, this.opts.masjid.timezone);
    // A live khutbah covers everything on the screens it's for (a screen that only listens doesn't show it).
    const id = this.opts.screenId;
    const kh = this.opts.mode === 'editor' ? null : this.bcList.find((b) => b.kind === 'khutbah' && isBroadcastLive(b, now) && (!id || !b.screenIds || b.screenIds.includes(id))) ?? null;
    if (this.kh.id !== 'sample') this.kh.set(kh, this.opts.masjid.timezone, now, this.opts.mode !== 'screen');
    // An emergency shows even while screens are blanked for salah (but not when a screen is switched off), and over
    // a live khutbah.
    this.bcs.root.style.zIndex = this.bcs.root.querySelector('.urgent') ? (this.kh.visible ? '1200' : '1000') : '';
  }

  setPower(state: PowerState) {
    this.powerState = state;
    this.power.classList.toggle('on', state !== 'on');
    this.power.classList.toggle('idle', state === 'idle');
    this.powerClock.style.display = state === 'idle' ? '' : 'none';
    this.tick(false);
  }

  /** Shows a design. Unchanged widgets are left alone; changed ones update in place. */
  setDesign(design: Design) {
    const prev = this.design;
    this.design = design;
    const m = gridMetrics(design);
    this.placeFooter();
    if (!prev || prev.aspect !== design.aspect) this.layout();
    this.stage.style.width = `${m.width}px`;
    this.stage.style.height = `${m.height}px`;
    this.renderBackground(design);

    const seen = new Set<string>();
    design.widgets.forEach((w, z) => {
      seen.add(w.id);
      const lay = this.layoutOf(w, design);
      const px = rectToPx(lay, m);
      const base = JSON.stringify([w.type, w.props, w.text, w.style, w.accent, lay.radius, w.fill, w.textColor, w.border, w.opacity, lay.w, lay.h, design.cols, design.rows, design.aspect, design.gap, design.margin, design.background]);
      const sig = `${base}|${w.fit}`;
      let mt = this.mounted.get(w.id);
      if (mt && mt.w.type !== w.type) { mt.view.destroy(); mt.root.remove(); this.mounted.delete(w.id); mt = undefined; }
      if (!mt) {
        const root = el('div', 'nvd-w'), surf = el('div', 'nvd-surf'), content = el('div', 'nvd-c');
        root.dataset.id = w.id;
        root.append(surf, content);
        const view = FACTORIES[w.type](content);
        mt = { w, sig: '', root, surf, content, view, env: this.envFor(w, px) };
        this.mounted.set(w.id, mt);
      }
      const r = mt.root;
      r.style.left = `${px.left}px`; r.style.top = `${px.top}px`; r.style.width = `${px.width}px`; r.style.height = `${px.height}px`;
      // The makruh banner sits in front of everything else on the screen, and shows only during a makruh time.
      r.style.zIndex = String(w.type === 'makruh' ? design.widgets.length + 10 : z + 1);
      r.style.display = w.hidden || !this.makruhShows(w) ? 'none' : '';
      // Added once and never moved: z-index (above) sets the order, and moving an element in the page restarts its
      // animations (the scrolling banner would jump back every time the design arrives again, e.g. on each refresh).
      if (r.parentElement !== this.stage) this.stage.insertBefore(r, this.overlay);
      if (sig !== mt.sig && base === mt.base) {
        // Only Content size moved (the slider): nothing to redraw, just the size, from the largest size already found.
        mt.w = w; mt.sig = sig;
        this.fit(mt);
      } else if (sig !== mt.sig) {
        mt.w = w; mt.sig = sig; mt.base = base; mt.env = this.envFor(w, px);
        const look = widgetLook(w, design.background);
        const bw = Math.max(0, Math.min(20, w.border?.width ?? 0));
        r.className = `nvd-w s-${w.style} nvd-al-${w.text.align} nvd-tc-${w.text.case}${look.fill ? ' has-fill' : ''}${w.textColor ? ' has-ink' : ''}${bw ? ' has-b' : ''}`;
        const ink = look.ink, dark = ink[0] + ink[1] + ink[2] < 384;
        const vars: Record<string, string> = {
          '--a': look.accent, '--a-ink': rgbToHex(look.accentInk), '--a-ink2': rgba(look.accentInk, 0.88), '--acc-text': look.accentText,
          '--ink': rgbToHex(ink), '--ink2': rgba(ink, dark ? 0.7 : 0.74), '--ink3': rgba(ink, dark ? 0.48 : 0.5),
          '--tile': rgba(ink, dark ? 0.06 : 0.07), '--line': rgba(ink, dark ? 0.12 : 0.14),
          '--bw': `${bw}px`, '--bc': w.border?.color ?? '#FFFFFF',
        };
        // A background pattern stays behind the widget: with no background of its own, the widget's rings, tracks,
        // tiles and softer text are drawn solid (the same colour they'd look on the plain background), so the pattern
        // never shows through them as though it were on top.
        if (design.background.pattern && (w.style === 'none' || w.opacity === 0)) {
          const solid = (a: number) => rgbToHex(mix(ink, look.surface, a));
          Object.assign(vars, { '--ink2': solid(dark ? 0.7 : 0.74), '--ink3': solid(dark ? 0.48 : 0.5), '--tile': solid(dark ? 0.06 : 0.07), '--line': solid(dark ? 0.12 : 0.14) });
        }
        // Makruh Times is an alert: always solid red with white text, whatever the screen's style.
        if (w.type === 'makruh') {
          r.className = `nvd-w s-flat nvd-makruh nvd-al-left nvd-tc-${w.text.case}`;
          Object.assign(vars, { '--ink': MAKRUH_INK, '--ink2': 'rgba(255,255,255,.86)', '--ink3': 'rgba(255,255,255,.7)', '--a': MAKRUH_INK, '--a-ink': MAKRUH_RED, '--acc-text': MAKRUH_INK, '--bw': '0px' });
          r.style.setProperty('--op', '1');
        }
        if (look.fill && w.type !== 'makruh') Object.assign(vars, { '--f': look.fill.base, '--f-hi': look.fill.hi, '--f-lo': look.fill.lo, '--f-ga': look.fill.glassA, '--f-gb': look.fill.glassB });
        else for (const k of ['--f', '--f-hi', '--f-lo', '--f-ga', '--f-gb']) r.style.removeProperty(k);
        for (const [k, v] of Object.entries(vars)) r.style.setProperty(k, v);
        r.style.setProperty('--op', String(Math.max(0, Math.min(100, w.opacity)) / 100));
        r.style.setProperty('--fw', String(w.text.weight));
        r.style.setProperty('--r', `${cornerRadius(w, m)}px`);
        r.style.setProperty('--nvd-font', fontStack(w.text.font));
        // The numbers can have their own font and colour (e.g. green seven-segment digits), labels and dates not.
        const dg = w.props as { digitFont?: string | null; digitColor?: string | null };
        // The Digital LED design: seven-segment digits (red unless a colour is picked).
        const led = isLed(w);
        const df = dg.digitFont || (led ? LED_FONT : null), dc = dg.digitColor || (led ? LED_DEFAULT : null);
        if (df) r.style.setProperty('--nvd-digits', fontStack(df)); else r.style.removeProperty('--nvd-digits');
        if (dc) r.style.setProperty('--nvd-digit-ink', dc); else r.style.removeProperty('--nvd-digit-ink');
        r.classList.toggle('nvd-led', led);
        // Clock and Countdown have no highlight colour of their own, so their accents follow the digits. Prayer times
        // keep their Next prayer highlight colour.
        r.classList.toggle('nvd-led-ink', led && w.type !== 'prayerTimes');
        r.style.fontFamily = 'var(--nvd-font)';
        r.style.setProperty('--fs', `${w.text.size}px`);
        mt.view.update(w, mt.env);
        mt.view.tick(this.tickData(), mt.env);
        this.fit(mt);
      } else mt.w = w;
    });
    for (const [id, mt] of this.mounted) if (!seen.has(id)) { mt.view.destroy(); mt.root.remove(); this.mounted.delete(id); }
  }

  /** Where a widget is drawn: the makruh banner takes the news ticker's place when it's set to (and there is one). */
  private layoutOf(w: Widget, design: Design): Widget {
    if (w.type !== 'makruh' || (w.props as MakruhProps).place === 'own') return w;
    const t = design.widgets.find((x) => x.type === 'ticker' && !x.hidden);
    return t ? { ...w, x: t.x, y: t.y, w: t.w, h: t.h, radius: t.radius } : w;
  }

  /** Whether the makruh banner shows now: always in the Studio, otherwise only during a makruh time. */
  private makruhShows(w: Widget): boolean {
    if (w.type !== 'makruh' || this.opts.mode === 'editor') return true;
    return !!makruhAt(this.opts.masjid, this.timetable, this.now(), w.props as MakruhProps);
  }

  private envFor(w: Widget, px?: { width: number; height: number }): Env {
    const box = px ?? (this.design ? rectToPx(w, gridMetrics(this.design)) : { width: 100, height: 100 });
    return { masjid: this.opts.masjid, timetable: this.timetable, mode: this.opts.mode, box: { width: box.width, height: box.height } };
  }

  private bgSig = '';
  private renderBackground(d: Design) {
    const b = d.background;
    const sig = JSON.stringify([b, d.aspect]);
    if (sig === this.bgSig) return;
    this.bgSig = sig;
    this.bg.replaceChildren();
    this.bg.style.background = '';
    if (b.kind === 'solid') this.bg.style.background = b.color;
    else if (b.kind === 'gradient') this.bg.style.background = `radial-gradient(120% 120% at 80% -10%, ${b.color} 0%, ${b.color2 ?? '#000000'} 70%)`;
    else if (b.image) { const img = el('img'); img.alt = ''; img.src = b.image; this.bg.style.background = b.color; this.bg.append(img); }
    if (b.pattern) this.bg.append(patternLayer(b.pattern, gridMetrics(d)));
    this.stage.classList.toggle('on-light', isLightBackground(b));
  }

  /**
   * Text sizing. With "scale text with widget" on, text grows and shrinks with the widget from its set size at the
   * widget's default dimensions. Either way, text is shrunk just enough that it never spills out of the widget.
   */
  private fit(mt: Mounted) {
    if (mt.w.hidden || !this.design) return;
    const def = WIDGETS[mt.w.type];
    const m = gridMetrics(this.design);
    const c = mt.content, root = mt.root;
    const scalable = canScaleContent(mt.w.type, designOf(mt.w));
    const graphics = () => [...c.querySelectorAll<HTMLElement | SVGElement>('.dial-box,.hg,.th,.pt2-dial,.ring,.pf-ic,.qr,.pt2-ic,.sa-svg,.db-wall,.dg-svg,.lg-pic')];
    // Parts laid out side by side or one above another inside a row or tile. If any two of them overlap, the text is
    // too big for its row even though nothing spills out of the widget (grid columns can shrink below their text).
    // Found only when something has to be measured: reading every part's style is slow, and most calls (a clock
    // ticking, the Content size slider) reuse a size found before.
    let groupsCache: Array<{ row: HTMLElement; kids: HTMLElement[] }> | null = null;
    const groupsOf = () => {
      if (groupsCache) return groupsCache;
      const groups: Array<{ row: HTMLElement; kids: HTMLElement[] }> = [];
      for (const row of [c, ...c.querySelectorAll<HTMLElement>('.nvd-center,.dn,.dn-row,.dn-l,.dn-r,.dn-foot,.dn-cards,.dn-card,.dn-ph,.dn-prog,.ev,.pt2-i,.pt-t,.pf-r,.pf-hero,.pf-in,.jf-b,.jf-row,.evc-i,.evt-i,.ev-i,.anl-i,.ang-i,.eva-i,.evs-hero,.evs-r,.evy-i,.sc-bar,.pt-head,.ck-stack,.st-sec,.hg-w,.th-w,.fl-row')]) {
        const kids = [...row.children].filter((k): k is HTMLElement => k instanceof HTMLElement && getComputedStyle(k).position !== 'absolute');
        if (kids.length) groups.push({ row, kids });
      }
      // Anything that hides its overflow (lists, card grids) must still show all of its parts.
      for (const n of c.querySelectorAll<HTMLElement>('div')) {
        if (n.closest('.tk-win,.rot') || groups.some((g) => g.row === n)) continue;
        const o = getComputedStyle(n).overflow;
        if (o !== 'hidden' && o !== 'clip') continue;
        const kids = [...n.children].filter((k): k is HTMLElement => k instanceof HTMLElement && getComputedStyle(k).position !== 'absolute');
        if (kids.length) groups.push({ row: n, kids });
      }
      groupsCache = groups;
      return groups;
    };
    // Side-by-side parts also keep a small gap, so a name never runs straight into its time.
    const tol = Math.max(0.5, 2 * this.scale), gap = 6 * this.scale;
    const collide = () => {
      for (const { row, kids } of groupsOf()) {
        const rs = kids.map((k) => k.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
        // Every part stays inside its row (rows centre their content, so text can spill out on both sides).
        const box = row.getBoundingClientRect();
        if (box.width > 0) for (const r of rs) if (r.left < box.left - tol || r.right > box.right + tol || r.top < box.top - tol || r.bottom > box.bottom + tol) return true;
        for (let i = 0; i < rs.length; i++) for (let j = i + 1; j < rs.length; j++) {
          const a = rs[i]!, b = rs[j]!;
          if (Math.min(a.right, b.right) - Math.max(a.left, b.left) > tol && Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > tol) return true;
        }
      }
      return false;
    };
    let floor: Array<[Element, number]> = [];
    const overflows = () => {
      if (c.scrollHeight > c.clientHeight + 1 || c.scrollWidth > c.clientWidth + 1) return true;
      for (const n of c.querySelectorAll<HTMLElement>('.pt-s,.ck-time,.big,.tx,.dn-v,.dn-v2,.dn-big,.dn-t,.dn-k,.pt-n,.ev-t,.nvd-label,.slot,.tk-tag,.st-h,.pf-time')) {
        if (n.offsetParent !== null && n.scrollWidth > n.clientWidth + 1) return true;
      }
      // Text clipped inside a tile (tiles hide their overflow, so check them directly).
      for (const t of c.querySelectorAll<HTMLElement>('.pt-t,.pt2-i,.pf-r,.pf-hero,.jf-b,.evc-i,.anl-i,.ang-i,.ev-d,.tk-tag,.nvd-alert')) {
        if (t.offsetParent !== null && (t.scrollWidth > t.clientWidth + 1 || t.scrollHeight > t.clientHeight + 1)) return true;
      }
      // Text that ends in "…" when cut shows it for even a fraction of a pixel, so these get no tolerance across.
      // scrollWidth rounds to whole pixels, so the text itself is measured.
      const range = document.createRange();
      for (const n of c.querySelectorAll<HTMLElement>('.nvd-label,.pt-n,.ev-t,.ev-m')) {
        if (n.offsetParent === null) continue;
        if (n.scrollHeight > n.clientHeight + 1) return true;
        range.selectNodeContents(n);
        if (range.getBoundingClientRect().width > n.getBoundingClientRect().width + 0.05) return true;
      }
      // Message lines wrap, so a word that's too long or a body cut short shows as overflow in either direction.
      for (const n of c.querySelectorAll<HTMLElement>('.bm-h,.bm-b,.lg-name')) {
        if (n.offsetParent === null) continue;
        // Big tight-set type pokes a little past its own line box, so a cut-off line is a whole line's height.
        const slack = Math.max(1, parseFloat(getComputedStyle(n).fontSize) * 0.4);
        if (n.scrollWidth > n.clientWidth + 1 || n.scrollHeight > n.clientHeight + slack) return true;
      }
      // The slide on show must fit in its box (slides centre their text, so it would spill out of both edges).
      for (const n of c.querySelectorAll<HTMLElement>('.rot>.it.on')) if (n.scrollHeight > n.clientHeight + 1 || n.scrollWidth > n.clientWidth + 1) return true;
      const run = c.querySelector<HTMLElement>('.tk-run');
      if (run && run.parentElement && run.offsetHeight > run.parentElement.clientHeight + 1) return true;
      // The ticker's tag may not crowd out the scrolling text.
      const win = c.querySelector<HTMLElement>('.tk-win');
      if (win && win.parentElement && win.offsetWidth < win.parentElement.clientWidth * 0.5) return true;
      if (collide()) return true;
      // Text may grow only while clocks, rings and other pictures keep most of their size.
      for (const [g, size] of floor) { const r = g.getBoundingClientRect(); if (Math.min(r.width, r.height) < size * 0.86) return true; }
      return false;
    };
    const setK = (k: number) => c.style.setProperty('--k', String(k));
    /**
     * The largest scale that fits inside the fixed inner margin. Remembered until the widget or the shape of its text
     * changes (a clock ticking from 12:01 to 12:02 keeps the same answer), so the once-a-second tick stays cheap.
     */
    const maxK = () => {
      const key = `${mt.base}|${this.scale.toFixed(3)}|${(c.textContent ?? '').replace(/\d/g, '0')}`;
      root.style.setProperty('--pad', `${FIT_PAD}px`);
      if (mt.kkey === key && mt.kmax != null) return mt.kmax;
      // The same widget at the same size gives the same answer anywhere (layout is at the screen's own resolution),
      // so a result found once is reused: reopening the Studio, its previews and the dashboard skip the measuring.
      const shared = `${mt.base}|${(c.textContent ?? '').replace(/\d/g, '0')}`;
      const known = FIT_CACHE.get(shared);
      if (known != null) { mt.kkey = key; mt.kmax = known; return known; }
      setK(0.2);
      floor = graphics().map((g) => { const r = g.getBoundingClientRect(); return [g, Math.min(r.width, r.height)] as [Element, number]; });
      let lo = 0.05, hi = 0.2;
      if (!overflows()) { lo = 0.2; hi = 0.4; setK(hi); while (!overflows() && hi < 24) { lo = hi; hi *= 2; setK(hi); } }
      for (let i = 0; i < 10; i++) { const mid = (lo + hi) / 2; setK(mid); if (overflows()) hi = mid; else lo = mid; }
      floor = [];
      // Never below the automatic size, so 100% is always at least as big as Automatic.
      const top = Math.max(lo, autoK());
      root.style.setProperty('--pad', `${FIT_PAD}px`);
      mt.kkey = key; mt.kmax = top;
      FIT_CACHE.set(shared, top);
      if (FIT_CACHE.size > 800) FIT_CACHE.delete(FIT_CACHE.keys().next().value!);
      return top;
    };

    c.style.transform = '';
    /**
     * Automatic size: from the text size, growing with the widget when "scale text with widget" is on, and shrunk just
     * enough that nothing spills out.
     */
    const autoK = () => {
      root.style.removeProperty('--pad');
      const ak = `${mt.base}|${(c.textContent ?? '').replace(/\d/g, '0')}`;
      const known = AUTO_CACHE.get(ak);
      if (known != null) { setK(known); return known; }
      const found = measureAuto();
      AUTO_CACHE.set(ak, found);
      if (AUTO_CACHE.size > 800) AUTO_CACHE.delete(AUTO_CACHE.keys().next().value!);
      return found;
    };
    const measureAuto = () => {
      const ref = rectToPx({ x: 0, y: 0, w: Math.min(def.size.w, this.design!.cols), h: Math.min(def.size.h, this.design!.rows) }, gridMetrics({ ...this.design!, aspect: '16:9', cols: 12, rows: 8 }));
      const box = rectToPx(mt.w, m);
      // Compared with the widget's default size, turned round for tall widgets, so a portrait widget gets text as big
      // as the same space would get lying flat (designs rearrange into lists for tall shapes).
      const along = Math.min(box.width / ref.width, box.height / ref.height), across = Math.min(box.width / ref.height, box.height / ref.width);
      const k0 = mt.w.text.scale ? Math.max(0.3, Math.min(4, Math.max(along, across))) : 1;
      setK(k0);
      if (!overflows()) return k0;
      let lo = 0.15, hi = k0;
      for (let i = 0; i < 8; i++) { const mid = (lo + hi) / 2; setK(mid); if (overflows()) hi = mid; else lo = mid; }
      return lo;
    };

    if (scalable && mt.w.fit != null) {
      // Content size: a share of the largest size that fits. The content is laid out afresh at that size, so rows,
      // columns and gaps adapt as the slider moves, exactly as they do for a widget of that size. 100% is never
      // smaller than the automatic size.
      const f = Math.max(10, Math.min(100, mt.w.fit)) / 100;
      setK(maxK() * f);
      root.dataset.fill = String(Math.round(mt.w.fit));
      return;
    }

    const k = autoK();
    setK(k);
    // In the Studio, note what this looks like as a Content size, so the slider starts where the widget already is.
    if (scalable && this.opts.mode === 'editor') {
      const km = maxK();
      root.style.removeProperty('--pad');
      setK(k);
      root.dataset.fill = String(Math.max(10, Math.min(100, Math.round((k / km) * 100))));
    }
  }

  private tickData(): Tick {
    const now = this.now();
    const masjid = this.opts.masjid;
    const state = prayerState(masjid, this.timetable, now, (d) => this.dayCache(masjid, this.timetable, d));
    return { now, day: ymd(now, masjid.timezone), state };
  }

  /** Salah mode: everything fades out except a digital clock at the top, until the blank period ends. */
  private tickSalah(now: Date) {
    const masjid = this.opts.masjid;
    const allowed = this.opts.mode === 'screen' ? this.opts.salahBlank !== false : this.opts.mode !== 'editor' && this.opts.salahBlank === true;
    const b = allowed ? salahBlankAt(masjid, (d) => this.dayCache(masjid, this.timetable, d), now) : null;
    const on = !!b;
    if (on !== this.salahOn) { this.salahOn = on; this.salah.classList.toggle('on', on); }
    if (on) {
      const sm = masjid.prayer.salahMode;
      this.salah.dataset.pos = sm.clock;
      const txt = hhmm(now, masjid.timezone, { h12: sm.h12 });
      if (this.salahClock.textContent !== txt) this.salahClock.textContent = txt;
    }
  }

  private tick(refit: boolean) {
    if (!this.design) return;
    const t = this.tickData();
    this.tickSalah(t.now);
    this.tickBroadcasts(t.now);
    if (this.powerState === 'idle') this.powerClock.textContent = hhmm(t.now, this.opts.masjid.timezone);
    for (const mt of this.mounted.values()) {
      if (mt.w.hidden) continue;
      let shown = false;
      if (mt.w.type === 'makruh') {
        const on = this.makruhShows(mt.w);
        if (on !== (mt.root.style.display !== 'none')) { mt.root.style.display = on ? '' : 'none'; shown = on; }
        if (!on) continue;
      }
      try {
        const changed = mt.view.tick(t, mt.env) || shown;
        if (changed || refit) this.fit(mt);
      } catch (e) {
        // One faulty widget must never take the screen down.
        console.error('[noorview] widget tick failed', mt.w.type, e);
      }
    }
  }

  private schedule() {
    const ms = 1000 - (Date.now() % 1000) + 5;
    this.timer = setTimeout(() => { this.tick(false); this.schedule(); }, ms);
  }

  /**
   * The footer sits in the screen's bottom border (every design keeps a clear border of at least 2% of the screen),
   * so it never covers a widget and the background carries on behind it. Its text is about 1% of the screen's height
   * and faded: readable a metre or two away, practically invisible from across the hall. Not on thumbnails.
   */
  private placeFooter() {
    const show = this.opts.mode !== 'thumbnail' && !!this.design;
    this.foot.style.display = show ? '' : 'none';
    this.footName.textContent = this.opts.screenName ?? '';
    if (!show || !this.design) return;
    const m = gridMetrics(this.design);
    const short = Math.min(m.width, m.height);
    this.foot.style.height = `${m.margin}px`;
    this.foot.style.left = this.foot.style.right = `${m.margin}px`;
    this.foot.style.fontSize = `${Math.max(9, Math.round(short * 0.0105))}px`;
  }

  /** Scales the stage to its host with a single transform. */
  layout() {
    if (!this.design) return;
    const { width, height } = gridMetrics(this.design);
    const hw = this.host.clientWidth, hh = this.host.clientHeight;
    if (!hw || !hh) return;
    const s = this.opts.fit === 'cover' ? Math.max(hw / width, hh / height) : Math.min(hw / width, hh / height);
    const left = (hw - width * s) / 2, top = (hh - height * s) / 2;
    this.scale = s;
    this.stage.style.transform = `translate3d(${left}px,${top}px,0) scale(${s})`;
    if (this.opts.mode === 'editor') this.stage.style.borderRadius = `${12 / s}px`;
    this.opts.onScale?.({ scale: s, left, top, width, height });
  }

  /** Re-measures text (e.g. after web fonts finish loading). */
  /** Measures again from scratch (e.g. once web fonts have loaded and text has a new size). */
  refit() { FIT_CACHE.clear(); AUTO_CACHE.clear(); for (const mt of this.mounted.values()) { mt.kkey = undefined; this.fit(mt); } }

  destroy() {
    if (this.timer) clearTimeout(this.timer);
    this.ro?.disconnect();
    for (const mt of this.mounted.values()) mt.view.destroy();
    this.mounted.clear();
    this.kh.destroy();
    this.stage.remove();
    this.host.classList.remove('nvd-host');
  }
}
